import { createFileRoute } from "@tanstack/react-router";
import { MEMBERSHIP_LABEL, type MembershipState } from "@/lib/membership";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useState } from "react";
import { formatCpf } from "@/lib/cpf";
import { listMembers, setAdminRole, adminUpdateUserAccount } from "@/lib/admin.functions";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { toastProfileSaveError } from "@/lib/sensitive-field-errors";
import { SensitiveFieldsNotice } from "@/components/sensitive-fields-notice";
import { Check, X, Shield, ShieldOff, Pencil } from "lucide-react";
import { formatDateBR } from "@/lib/datetime";

export const Route = createFileRoute("/_authenticated/admin/membros")({
  component: AdminMembros,
});

type EditState = {
  id: string;
  fullName: string;
  email: string;
  password: string;
  confirm: string;
  cpf: string;
  birthDate: string;
} | null;

function AdminMembros() {
  const listFn = useServerFn(listMembers);
  const roleFn = useServerFn(setAdminRole);
  const updateAccountFn = useServerFn(adminUpdateUserAccount);
  const queryClient = useQueryClient();
  const [edit, setEdit] = useState<EditState>(null);

  const { data, isLoading } = useQuery({ queryKey: ["members"], queryFn: () => listFn() });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ["members"] });
    queryClient.invalidateQueries({ queryKey: ["pending-approvals"] });
    queryClient.invalidateQueries({ queryKey: ["pending-approvals-count"] });
  };

  const roleMutation = useMutation({
    mutationFn: (v: { userId: string; makeAdmin: boolean }) => roleFn({ data: v }),
    onSuccess: () => {
      invalidate();
      toast.success("Permissão atualizada");
    },
    onError: (e: any) => toast.error(e.message || "Erro"),
  });

  const accountMutation = useMutation({
    mutationFn: (v: { userId: string; email?: string; password?: string }) =>
      updateAccountFn({ data: v }),
    onSuccess: (res: any) => {
      invalidate();
      setEdit(null);
      toast.success(
        res?.changed?.length
          ? `Atualizado: ${res.changed.join(", ")}.`
          : "Nenhuma alteração aplicada.",
      );
    },
    onError: (e: any) => toastProfileSaveError(e, "Erro ao atualizar usuário"),
  });

  const submitEdit = () => {
    if (!edit) return;
    if (edit.password && edit.password.length < 8)
      return toast.error("A senha deve ter ao menos 8 caracteres.");
    if (edit.password && edit.password !== edit.confirm)
      return toast.error("As senhas não coincidem.");
    const original = data?.find((m: any) => m.id === edit.id) as any;
    // Nome, CPF e data de nascimento vêm da Gestão de cadastros e não são
    // alteráveis aqui: apenas e-mail de acesso e senha.
    accountMutation.mutate({
      userId: edit.id,
      email:
        edit.email.trim() && edit.email.trim() !== (original?.email ?? "")
          ? edit.email.trim()
          : undefined,
      password: edit.password || undefined,
    });
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-serif text-2xl font-semibold text-primary sm:text-3xl">Membros</h1>
        <p className="mt-1 text-sm text-muted-foreground sm:text-base">
          Aprove, bloqueie ou conceda permissão de administrador.
        </p>
      </div>

      {isLoading ? (
        <div className="min-h-[70vh] animate-pulse rounded-xl bg-secondary" />
      ) : (
        <ul className="grid gap-3">
          {data?.map((m: any) => {
            const isAdmin = m.roles.includes("admin");
            const approved = m.approved;
            const initials = (m.full_name ?? "?")
              .split(" ")
              .map((s: string) => s[0])
              .slice(0, 2)
              .join("")
              .toUpperCase();
            return (
              <li
                key={m.id}
                className="flex flex-col gap-3 rounded-xl border border-border bg-card p-4 sm:flex-row sm:items-center sm:justify-between"
              >
                <div className="flex min-w-0 items-center gap-3">
                  {m.avatar_url ? (
                    <img
                      src={m.avatar_url}
                      alt=""
                      className="h-11 w-11 shrink-0 rounded-full object-cover"
                    />
                  ) : (
                    <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-secondary font-medium text-primary">
                      {initials}
                    </div>
                  )}
                  <div className="min-w-0">
                    <p className="truncate font-medium text-foreground">
                      {m.full_name ?? "Sem nome"}
                    </p>
                    {m.email && <p className="truncate text-xs text-muted-foreground">{m.email}</p>}
                    <p className="text-xs text-muted-foreground">
                      Cadastro em {formatDateBR(m.created_at)}
                    </p>

                    <div className="mt-1.5 flex flex-wrap gap-1.5">
                      {isAdmin ? <Badge>Admin</Badge> : <Badge variant="secondary">Membro</Badge>}
                      {approved ? (
                        <Badge variant="outline" className="border-primary/30 text-primary">
                          Acesso liberado
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="border-gold/60 text-gold-strong">
                          {MEMBERSHIP_LABEL[
                            ((m as any).membership_status ?? "pending") as MembershipState
                          ] ?? "Sem acesso"}
                        </Badge>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2 sm:shrink-0 sm:justify-end">
                  <Button
                    size="sm"
                    variant={isAdmin ? "outline" : "default"}
                    disabled={roleMutation.isPending}
                    onClick={() => roleMutation.mutate({ userId: m.id, makeAdmin: !isAdmin })}
                  >
                    {isAdmin ? (
                      <>
                        <ShieldOff className="mr-1.5 h-4 w-4" /> Remover admin
                      </>
                    ) : (
                      <>
                        <Shield className="mr-1.5 h-4 w-4" /> Tornar admin
                      </>
                    )}
                  </Button>
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() =>
                      setEdit({
                        id: m.id,
                        fullName: m.full_name ?? "",
                        email: m.email ?? "",
                        password: "",
                        confirm: "",
                        cpf: formatCpf(m.cpf ?? ""),
                        birthDate: (m.birth_date ?? "").slice(0, 10).split("-").reverse().join("/"),
                      })
                    }
                  >
                    <Pencil className="mr-1.5 h-4 w-4" /> Editar
                  </Button>
                </div>
              </li>
            );
          })}
          {(!data || data.length === 0) && (
            <li className="rounded-xl border border-dashed border-border p-12 text-center text-muted-foreground">
              Nenhum membro cadastrado ainda.
            </li>
          )}
        </ul>
      )}

      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Editar usuário</DialogTitle>
            <DialogDescription>
              Altere o e-mail de acesso ou defina uma nova senha. Deixe a senha em branco para
              mantê-la. Os dados pessoais são exibidos apenas para consulta.
            </DialogDescription>
          </DialogHeader>
          {edit && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="edit-name">Nome completo</Label>
                <Input
                  id="edit-name"
                  value={edit.fullName || "—"}
                  readOnly
                  className="cursor-not-allowed bg-muted/50"
                  aria-describedby="edit-dados-bloqueados"
                />
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="edit-cpf">CPF</Label>
                  <Input
                    id="edit-cpf"
                    value={edit.cpf || "—"}
                    readOnly
                    className="cursor-not-allowed bg-muted/50"
                    aria-describedby="edit-dados-bloqueados"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-birth">Data de nascimento</Label>
                  <Input
                    id="edit-birth"
                    value={edit.birthDate || "—"}
                    readOnly
                    className="cursor-not-allowed bg-muted/50"
                    aria-describedby="edit-dados-bloqueados"
                  />
                </div>
              </div>
              <SensitiveFieldsNotice variant="admin" id="edit-dados-bloqueados" />
              <div className="space-y-2">
                <Label htmlFor="edit-email">E-mail</Label>
                <Input
                  id="edit-email"
                  type="email"
                  value={edit.email}
                  onChange={(e) => setEdit({ ...edit, email: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-pass">Nova senha (opcional)</Label>
                <Input
                  id="edit-pass"
                  type="password"
                  autoComplete="new-password"
                  value={edit.password}
                  onChange={(e) => setEdit({ ...edit, password: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-pass2">Confirmar nova senha</Label>
                <Input
                  id="edit-pass2"
                  type="password"
                  autoComplete="new-password"
                  value={edit.confirm}
                  onChange={(e) => setEdit({ ...edit, confirm: e.target.value })}
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEdit(null)}>
              Cancelar
            </Button>
            <Button onClick={submitEdit} disabled={accountMutation.isPending}>
              {accountMutation.isPending ? "Salvando..." : "Salvar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
